import { List } from "@mantine/core";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { bodyShopAtom } from "atoms/selectBodyShop";
import { vehicleConditionQuestionsAtom } from "atoms/vehicleCondition/questions";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { ClaimHandlingDecision } from "types/DigitalJourneySession";

export const DJHandlingDecisionPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [claimHandlingDecision, setClaimHandlingDecision] = useAtom(claimHandlingDecisionAtom);
  const vehicleConditionQuestions = useAtomValue(vehicleConditionQuestionsAtom);
  const setVehiclePhotos = useSetAtom(vehiclePhotosAtom);
  const setDamagePhotos = useSetAtom(damagePhotosAtom);
  const setSelectedBodyShop = useSetAtom(bodyShopAtom);

  const isDisabled = !claimHandlingDecision;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Claim Handling Decision - continue" });
    if (claimHandlingDecision === ClaimHandlingDecision.BODY_SHOP) {
      setDamagePhotos([]);
      setVehiclePhotos([]);
    }
    if (claimHandlingDecision === ClaimHandlingDecision.PHOTO_CAPTURE) {
      setSelectedBodyShop(null);
    }
    if (claimHandlingDecision === ClaimHandlingDecision.EXPERT) {
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
    }
    onNext();
  };

  const options = [
    {
      value: ClaimHandlingDecision.BODY_SHOP,
    },
    {
      value: ClaimHandlingDecision.EXPERT,
    },
    {
      value: ClaimHandlingDecision.PHOTO_CAPTURE,
    },
  ].filter((option) => {
    if (vehicleConditionQuestions.haveAirbagsDeployed || !vehicleConditionQuestions.isVehicleDrivable) {
      return option.value !== ClaimHandlingDecision.PHOTO_CAPTURE;
    }
    return option.value !== ClaimHandlingDecision.EXPERT;
  });

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          Come vuoi procedere?
        </Text>
        <Flex gap={16} role="radiogroup" aria-labelledby="typeOfClaim">
          {options.map((option) => (
            <Button
              aria-labelledby={`typeOfClaim-${option.value}-label`}
              aria-checked={claimHandlingDecision === option.value}
              role="radio"
              key={option.value}
              onClick={() => setClaimHandlingDecision(option.value)}
              typeVariant={claimHandlingDecision === option.value ? "primary" : "secondary"}
              sizeVariant="small"
              sx={{
                "& div": {
                  justifyContent: "flex-start",
                  fontWeight: 400,
                  color: claimHandlingDecision === option.value ? colors.white : colors.grey[90],
                },
              }}
            >
              <label id={`typeOfClaim-${option.value}-label`}>
                {getHandlingDecisionLabel(option.value, claimHandlingDecision === option.value)}
              </label>
            </Button>
          ))}
        </Flex>
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};

const getHandlingDecisionLabel = (decision: ClaimHandlingDecision, isSelected: boolean) => {
  switch (decision) {
    case ClaimHandlingDecision.EXPERT:
      return (
        <Flex
          sx={{
            "&& > *": {
              color: isSelected ? colors.white : colors.grey[90],
              textAlign: "start",
            },
          }}
        >
          <Text weight={700}>Vorrei procedere con un perito</Text>
          <Text>Selezionando questa opzione, un perito valuterà i danni al tuo veicolo</Text>
        </Flex>
      );
    case ClaimHandlingDecision.PHOTO_CAPTURE:
      return (
        <Flex
          sx={{
            "&& > *": {
              color: isSelected ? colors.white : colors.grey[90],
              textAlign: "start",
            },
          }}
        >
          <Text weight={700}>Vorrei procedere con la raccolta fotografica</Text>
          <Text>
            Selezionando questa opzione, potrai documentare i danni tramite la fotocamera del tuo smartphone.
          </Text>
          <Text>
            ⚠️ <strong>Nota tecnica</strong>: per motivi di sicurezza,{" "}
            <strong>il sistema consente esclusivamente lo scatto di foto in tempo reale</strong>. Non sarà
            possibile caricare immagini già presenti nella galleria del telefono.
          </Text>
          <Text>
            <strong>Il veicolo non è nelle vicinanze?</strong>
          </Text>
          <Text>
            Nessun problema:{" "}
            <strong>puoi chiudere la pagina ora e riprendere la denuncia in qualsiasi momento</strong> dal
            link che ti abbiamo inviato. I tuoi progressi sono salvati automaticamente.
          </Text>
        </Flex>
      );
    case ClaimHandlingDecision.BODY_SHOP:
      return (
        <Flex
          sx={{
            "&& > *": {
              color: isSelected ? colors.white : colors.grey[90],
              textAlign: "start",
            },
          }}
        >
          <Text weight={700}>Vorrei riparare il mio veicolo in una carrozzeria convenzionata</Text>
          <Text>Riparazioni garantite con i migliori standard qualitativi, senza anticipo di denaro.</Text>
          <List
            size="sm"
            listStyleType="disc"
            sx={{
              "&& div > *": {
                color: isSelected ? colors.white : colors.grey[90],
              },
            }}
          >
            <List.Item>
              <strong>Zero spese</strong> per la riparazione della tua auto;
            </List.Item>
            <List.Item>
              <strong>Servizi inclusi</strong>: Auto di cortesia, ritiro/riconsegna e lavaggio veicolo;
            </List.Item>
            <List.Item>
              <strong>Garanzia</strong> sulle riparazioni effettuate.
            </List.Item>
          </List>
          <Text>
            <em>
              Nota: Se hai in polizza questo servizio e decidi di non usufruirne, il danno verrà comunque
              risarcito, ma in base a quanto la Compagnia avrebbe corrisposto alla propria carrozzeria
              convenzionata. Per maggiori dettagli consulta il Set Informativo.
            </em>
          </Text>
        </Flex>
      );
    default:
      return <></>;
  }
};
